import {
  property,
  html,
  customElement,
  css,
  cssManager,
  type TemplateResult,
  type CSSResult,
} from '@design.estate/dees-element';
import { DeesThumbnailBase } from '../dees-thumbnail-shared/DeesThumbnailBase.js';
import { thumbnailBaseStyles } from '../dees-thumbnail-shared/styles.js';
import { demo } from './demo.js';

export interface IThumbnailFolderItem {
  type: 'pdf' | 'image' | 'audio' | 'video' | 'note' | 'folder' | 'unknown';
  thumbnailSrc?: string;
  name: string;
}

const TYPE_ICON_MAP: Record<string, string> = {
  pdf: 'lucide:FileText',
  image: 'lucide:Image',
  audio: 'lucide:Music',
  video: 'lucide:Video',
  note: 'lucide:FileCode',
  folder: 'lucide:Folder',
  unknown: 'lucide:File',
};

declare global {
  interface HTMLElementTagNameMap {
    'dees-thumbnail-folder': DeesThumbnailFolder;
  }
}

@customElement('dees-thumbnail-folder')
export class DeesThumbnailFolder extends DeesThumbnailBase {
  public static demo = demo;
  public static demoGroups = ['Media'];
  public static styles = [
    ...thumbnailBaseStyles,
    css`
      .folder-content {
        position: relative;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        background: var(--dees-color-bg-secondary);
        overflow: hidden;
      }

      .folder-header {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 14px 8px;
        flex-shrink: 0;
      }

      .folder-icon {
        font-size: 18px;
        color: var(--dees-color-accent-warning);
      }

      .folder-name {
        font-size: 12px;
        font-weight: 600;
        color: var(--dees-color-text-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        flex: 1;
      }

      .preview-grid {
        flex: 1;
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 1fr 1fr;
        gap: 4px;
        padding: 0 14px 14px;
        min-height: 0;
      }

      .grid-cell {
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        overflow: hidden;
        background: color-mix(in srgb, var(--dees-color-text-primary) 7%, var(--dees-color-bg-primary));
        position: relative;
      }

      .grid-cell img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }

      .grid-cell dees-icon {
        font-size: 20px;
        color: var(--dees-color-text-muted);
      }

      .grid-cell-empty {
        background: var(--dees-color-bg-tertiary);
      }

    `,
  ] as any;

  @property({ type: String })
  accessor name: string = '';

  @property({ attribute: false })
  accessor items: IThumbnailFolderItem[] = [];

  protected renderTileContent(): TemplateResult {
    const previewItems = this.items.slice(0, 4);
    const emptyCells = 4 - previewItems.length;

    return html`
      <div class="folder-content">
        <div class="folder-header">
          <dees-icon class="folder-icon" icon="lucide:Folder"></dees-icon>
          <div class="folder-name">${this.name || 'Untitled Folder'}</div>
        </div>

        <div class="preview-grid">
          ${previewItems.map((item) => html`
            <div class="grid-cell">
              ${item.thumbnailSrc ? html`
                <img loading="lazy" decoding="async" src="${item.thumbnailSrc}" alt="${item.name}" />
              ` : html`
                <dees-icon icon="${TYPE_ICON_MAP[item.type] || TYPE_ICON_MAP.unknown}"></dees-icon>
              `}
            </div>
          `)}
          ${Array.from({ length: emptyCells }).map(() => html`
            <div class="grid-cell grid-cell-empty"></div>
          `)}
        </div>
      </div>


    `;
  }

  protected renderBottomBar(): TemplateResult | string {
    if (!this.label && !this.items.length) return '';
    return html`
      <div class="tile-info-bar">
        ${this.label ? html`<span class="info-label" title="${this.label}">${this.label}</span>` : ''}
        <span class="info-spacer"></span>
        <span class="info-detail">${this.items.length} item${this.items.length !== 1 ? 's' : ''}</span>
      </div>
    `;
  }

  protected getTileClickDetail(): Record<string, unknown> {
    return {
      name: this.name,
      itemCount: this.items.length,
      items: this.items,
    };
  }
}
